import { useNavigation } from '@react-navigation/native'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Pressable, StyleSheet, View } from 'react-native'
import { Button, Card, Chip, Divider, Text } from 'react-native-paper'
import { useAuthContext } from '../../../../contexts/auth-context'
import {
  ContributionStatus,
  ContributionType,
  TargetType,
  useUpdateContributionStatusMutation,
} from '../../../../generated/graphql'
import { useTheme } from '../../../../hooks/theme.hook'
import { AppNavigation, ReplayNavigation } from '../../../../navigation/types'
import { UserLink } from '../../../../@ui/typo/user.link'
import { TaskPendingIcon } from '../../../tasks/components/task-pending-icon'
import { MelddTaskTargetType } from '../../../tasks/task.types'
import { Contribution } from '../types'
import { BumpButtons, BumpButtonsReadOnly } from '../bump/bump.buttons'
import { ContributionDecisionBox } from './contribution-decision.box'
import { createStyle } from '../../../../contexts/global-styles.provider'
import Toast from 'react-native-root-toast'
import { MessageType } from '../../../../utils/message-type'
import * as Sentry from '@sentry/react-native'
import Row from '../../../../@ui/containers/grid/row'
import { ReplayButton } from '../../replay/components/reply.button'
import { ContributionTypeChip } from './contribution-type.chip'
import { ContributionStatusChip } from './contribution-status.chip'
interface ContributionCardProps {
  contribution: Contribution
  showStatus?: boolean
  children?: React.ReactNode
  readonly?: boolean
}

const ContributionInformation = ({
  contribution,
}: {
  contribution: Contribution
}) => {
  const { t } = useTranslation('contributions')
  const styles = useStyles(contribution)
  return (
    <>
      {[ContributionType.Edit, ContributionType.OwnerEdit].includes(
        contribution.contributionType
      ) && (
        <>
          <View style={styles.header}>
            <Text variant={'titleMedium'} numberOfLines={1}>
              {t('Proposed text')}:
            </Text>
          </View>
          <View style={styles.header}>
            <Text style={styles.contributionText}>{contribution.input}</Text>
          </View>
          <View style={styles.header}>
            <Text variant={'titleMedium'} numberOfLines={1}>
              {t('Justification')}:
            </Text>
          </View>
          <View style={styles.header}>
            <Text style={styles.contributionMetaText}>
              {contribution.motivation}
            </Text>
          </View>
        </>
      )}
      {contribution.contributionType === ContributionType.Inquiry && (
        <>
          <View style={styles.header}>
            <Text variant={'titleMedium'} numberOfLines={1}>
              {t('Inquiry')}:
            </Text>
          </View>
          <View style={styles.header}>
            <Text style={styles.contributionMetaText}>
              {contribution.input}
            </Text>
          </View>
        </>
      )}
      {contribution.contributionType === ContributionType.Challenge && (
        <>
          <View style={styles.header}>
            <Text variant={'titleMedium'} numberOfLines={1}>
              {t('Challenge')}:
            </Text>
          </View>
          <View style={styles.header}>
            <Text style={styles.contributionMetaText}>
              {contribution.input}
            </Text>
          </View>
          <View style={styles.header}>
            <Text variant={'titleMedium'} numberOfLines={1}>
              {t('Reference')}:
            </Text>
          </View>
          <View style={styles.header}>
            <Text style={styles.contributionMetaText}>
              {contribution.motivation}
            </Text>
          </View>
        </>
      )}
    </>
  )
}

export const CancelContribution = ({
  contribution,
}: {
  contribution: {
    id: string
    status: ContributionStatus
    userId: string
    contributionType: ContributionType
  }
}) => {
  const { t } = useTranslation('contributions')
  const styles = useStyles(contribution)
  const { userId } = useAuthContext()
  const theme = useTheme()
  const [doUpdateContributionStatus] = useUpdateContributionStatusMutation()
  const canCancel = useMemo(() => {
    return (
      contribution.status === ContributionStatus.Pending &&
      contribution.userId == userId
    )
  }, [contribution, userId])

  const cancelContribution = useCallback(async () => {
    const input = {
      contributionId: contribution.id,
      status: ContributionStatus.Cancelled,
    }

    try {
      await doUpdateContributionStatus({
        variables: { input },
        onQueryUpdated: (query: any) => {
          if (query.queryName == 'contributionsForMyArticles') {
            query.refetch()
          }
        },
      })
      return Toast.show(t('contribution-cancelled'), MessageType.info)
    } catch (e) {
      Toast.show((e as Error).message, MessageType.error)
      Sentry.captureException(e)
      console.error(e)
    }
  }, [doUpdateContributionStatus, contribution])

  if (!canCancel) return null

  return (
    <Button
      onPress={() => {
        cancelContribution()
      }}
      mode={'outlined'}
      style={styles.cancelButton}
      textColor={theme.colors.danger.main}
    >
      {t('Cancel')}
    </Button>
  )
}

export function ContributionCard(props: ContributionCardProps) {
  const { contribution, readonly } = props
  const { userId } = useAuthContext()
  const navigation = useNavigation<AppNavigation | ReplayNavigation>()

  const { t } = useTranslation('contributions')

  const styles = useStyles(contribution)

  const canCancel = useMemo(() => {
    return (
      contribution.status === ContributionStatus.Pending &&
      contribution.userId == userId &&
      !readonly
    )
  }, [contribution, readonly, userId])

  return (
    <Card style={[styles.container]} mode={'outlined'}>
      <Card.Content>
        <View style={styles.cardHeader}>
          <Pressable
            onPress={() => {
              if (readonly) {
                ;(navigation as ReplayNavigation).navigate(
                  'ReplayPerspective',
                  {
                    articleId: contribution.article.id,
                  }
                )
              } else {
                ;(navigation as AppNavigation).navigate('Perspective', {
                  articleId: contribution.article.id,
                })
              }
            }}
            style={{ flexShrink: 1 }}
          >
            <Text
              variant={'titleLarge'}
              numberOfLines={4}
              ellipsizeMode={'tail'}
              style={styles.title}
            >
              {contribution.article.title}
            </Text>
          </Pressable>
          {props.children ? props.children : null}
        </View>
        <View>
          {contribution.contributionType !== ContributionType.OwnerEdit && (
            <View style={[styles.header, { marginBottom: 20 }]}>
              <Text variant={'titleMedium'} numberOfLines={1}>
                {t('Status')}:
              </Text>
              <ContributionStatusChip contribution={contribution} />
            </View>
          )}
          {contribution.status === ContributionStatus.Accepted &&
            contribution.contributionType !== ContributionType.OwnerEdit &&
            contribution.user && (
              <View style={[styles.header, { marginBottom: 20 }]}>
                <Text variant={'titleMedium'} numberOfLines={1}>
                  {t('contributor')}:
                </Text>
                <UserLink user={contribution.user} />
              </View>
            )}
        </View>
        <View style={styles.header}>
          <Text variant={'titleMedium'} numberOfLines={1}>
            {t('Type of contribution')}:
          </Text>
          <ContributionTypeChip contribution={contribution} />
        </View>
        <View style={[styles.header, { marginTop: 10 }]}>
          <Text variant={'titleMedium'} numberOfLines={1}>
            {t('Original text')}:
          </Text>
        </View>
        <View style={styles.header}>
          <Text style={styles.contributionText}>
            {contribution.originalText}
          </Text>
        </View>
        <ContributionInformation contribution={contribution} />
        {contribution.response && (
          <>
            <View style={[styles.header]}>
              <Text variant={'titleMedium'} numberOfLines={1}>
                {t('Response')}:
              </Text>
            </View>
            <View>
              <Text style={styles.contributionMetaText}>
                {contribution.response}
              </Text>
            </View>
          </>
        )}
      </Card.Content>
      {contribution.article.isOwner && !readonly && (
        <>
          <Card.Content>
            <ContributionDecisionBox contribution={contribution} />
          </Card.Content>
          <Divider style={styles.divider} />
        </>
      )}
      {canCancel && (
        <>
          <Card.Content>
            <View style={{ width: 120 }}>
              <CancelContribution contribution={contribution} />
            </View>
          </Card.Content>
          <Divider style={styles.divider} />
        </>
      )}

      {/* // Contributions have replay and task functionality but is hidden for now */}
      {false && !readonly && (
        <>
          <Card.Content>
            <Row justifyContent="space-between" alignItems="center" wrap>
              <Row alignItems="center" columnGap={2}>
                <ReplayButton
                  size={26}
                  targetId={contribution.id}
                  targetType={TargetType.Contribution}
                />

                <TaskPendingIcon
                  size={36}
                  targetId={contribution.id}
                  targetType={MelddTaskTargetType.CONTRIBUTION_TASK}
                />
              </Row>
            </Row>
          </Card.Content>
          <Divider style={styles.divider} />
        </>
      )}
      <Card.Content>
        {!readonly ? (
          <BumpButtons contribution={contribution} />
        ) : (
          <BumpButtonsReadOnly contribution={contribution} />
        )}
      </Card.Content>
    </Card>
  )
}

const useStyles = (contribution: {
  status: ContributionStatus
  contributionType: ContributionType
}) => {
  const hook = createStyle(({ theme }) => {
    const statusColor =
      theme.colors[
        contribution.status.toLowerCase() as
          | 'accepted'
          | 'rejected'
          | 'pending'
          | 'cancelled'
          | 'censured'
      ]

    const contributionBkgColor =
      theme.colors[
        (contribution.contributionType.toLowerCase() + '50') as
          | 'challenge50'
          | 'edit50'
          | 'inquiry50'
          | 'reserved50'
      ]
    return {
      container: {
        marginBottom: theme.spacing(4),
      },
      cardHeader: {
        alignItems: 'center',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(4),
      },
      header: {
        flexDirection: 'row',
        alignItems: 'center',
        columnGap: theme.spacing(2),
        marginBottom: theme.spacing(2),
      },
      title: {
        paddingTop: theme.spacing(4),
        flex: 1,
        textDecorationLine: 'underline',
      },
      statusChip: {
        borderRadius: 20,
        backgroundColor: statusColor,
        padding: 0,
      },
      statusChipLabel: {
        color: 'white',
        fontSize: 12,
        lineHeight: 15,
      },
      contributionText: {
        padding: theme.spacing(1),
        backgroundColor: contributionBkgColor,
        marginBottom: theme.spacing(5),
      },
      contributionMetaText: {
        padding: theme.spacing(1),
        marginBottom: theme.spacing(5),
      },
      cancelButton: {
        borderColor: theme.colors.danger.main,
        flex: 1,
      },
      divider: {
        backgroundColor: '#767680',
        marginVertical: theme.spacing(4),
      },
    }
  })
  return hook()
}
